// ============================================================
// ContactMessagesPage.js
// Prospect enquiries from the marketing /contact form.
//
// These were insert-only from the day the form shipped: the row
// committed and nothing in the platform could read it back. Delivery
// was the operator email + the Teams lead channel only, so a missed
// email meant the enquiry was gone from the product.
//
// A CARD LIST rather than the DataTable used elsewhere in the portal:
// the message body IS the content here (up to 2000 characters), and a
// table cell would truncate the only part worth reading.
//
// Deliberately NOT a CRM. No replying from the portal (email is the
// reply channel), no assignment, no pipeline stages. Two states —
// waiting for someone, or dealt with.
//
// URL state: ?status=new|handled
// ============================================================

import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import Badge from '../components/Badge';
import { toast } from '../components/Toaster';
import { listAdminContactMessages, patchAdminContactMessage } from '../api/adminContactMessages';
import { translateError } from '../utils/errorMessages';
import { formatDate } from '../utils/format';
import StatusMessage from '../components/StatusMessage';
import './ContactMessagesPage.css';

const STATUS_OPTIONS = [
  { value: 'new', label: 'Waiting' },
  { value: 'handled', label: 'Dealt with' },
];

export default function ContactMessagesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlStatus = searchParams.get('status') || '';

  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ new: 0, handled: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [noteDrafts, setNoteDrafts] = useState({});

  const fetchData = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await listAdminContactMessages({ status: urlStatus || undefined, pageSize: 200 });
      setItems(res.items || []);
      setCounts(res.statusCounts || { new: 0, handled: 0 });
      // Reseed the note boxes from the server every load, so a failed
      // save never leaves an unsaved note looking saved.
      const seeded = {};
      (res.items || []).forEach((m) => { seeded[m.contactMessageId] = m.notes || ''; });
      setNoteDrafts(seeded);
    } catch (err) {
      setError(translateError(err));
    } finally { setLoading(false); }
  }, [urlStatus]);

  useEffect(() => { fetchData(); }, [fetchData]);

  function handleStatusChange(next) {
    const p = new URLSearchParams(searchParams);
    if (next) p.set('status', next); else p.delete('status');
    setSearchParams(p);
  }

  async function patch(message, body, successMessage) {
    setBusyId(message.contactMessageId);
    try {
      await patchAdminContactMessage(message.contactMessageId, body);
      toast.success(successMessage);
      await fetchData();
    } catch (err) {
      toast.error(translateError(err));
    } finally { setBusyId(null); }
  }

  const card = (m) => {
    const busy = busyId === m.contactMessageId;
    const handled = m.status === 'handled';
    const draft = noteDrafts[m.contactMessageId] ?? '';
    const noteChanged = draft !== (m.notes || '');
    return (
      <article key={m.contactMessageId} className="contact-msg" data-testid={`contact-${m.contactMessageId}`}>
        <header className="contact-msg__head">
          <div>
            <span className="contact-msg__who">{`${m.firstName} ${m.lastName}`.trim()}</span>
            <span className="contact-msg__org">{m.organisationName}</span>
          </div>
          <Badge variant={handled ? 'success' : 'warning'}>
            {handled ? 'Dealt with' : 'Waiting'}
          </Badge>
        </header>

        <div className="contact-msg__contact">
          <a href={`mailto:${m.email}`}>{m.email}</a>
          {m.phone && <a href={`tel:${m.phone}`}>{m.phone}</a>}
          <span className="contact-msg__muted">{formatDate(m.submittedAt)}</span>
        </div>

        <p className="contact-msg__body">{m.message}</p>

        {handled && m.handledBy && (
          <p className="contact-msg__muted">
            Dealt with by {m.handledBy}{m.handledAt ? ` on ${formatDate(m.handledAt)}` : ''}
          </p>
        )}

        <label className="contact-msg__note">
          <span className="contact-msg__muted">Internal note</span>
          <textarea
            rows={2}
            maxLength={4000}
            value={draft}
            placeholder="Anything the other person needs to know"
            onChange={(e) => setNoteDrafts((d) => ({ ...d, [m.contactMessageId]: e.target.value }))}
            data-testid={`contact-note-${m.contactMessageId}`}
          />
        </label>

        <div className="contact-msg__actions">
          {!handled && (
            <button
              type="button" className="contact-msg__btn contact-msg__btn--primary" disabled={busy}
              onClick={() => patch(m, { status: 'handled' }, 'Marked as dealt with.')}
              data-testid={`contact-handle-${m.contactMessageId}`}
            >
              {busy ? 'Saving…' : 'Mark dealt with'}
            </button>
          )}
          {handled && (
            <button
              type="button" className="contact-msg__btn" disabled={busy}
              onClick={() => patch(m, { status: 'new' }, 'Reopened.')}
              data-testid={`contact-reopen-${m.contactMessageId}`}
            >
              Reopen
            </button>
          )}
          {noteChanged && (
            <button
              type="button" className="contact-msg__btn" disabled={busy}
              onClick={() => patch(m, { notes: draft.trim() || null }, 'Note saved.')}
              data-testid={`contact-save-note-${m.contactMessageId}`}
            >
              Save note
            </button>
          )}
        </div>
      </article>
    );
  };

  return (
    <>
      <PageHeader
        title="Contact Messages"
        subtitle="Enquiries from the contact form on a2zcarehub.co.uk. Reply by email; mark them here so nobody replies twice."
      />

      <div className="clients-page__toolbar">
        <div className="clients-page__filters">
          <label className="clients-page__tenant-filter">
            <span className="clients-page__tenant-label">Status</span>
            <select
              value={urlStatus}
              onChange={(e) => handleStatusChange(e.target.value)}
              className="clients-page__tenant-select"
              data-testid="contact-status-filter"
            >
              <option value="">All ({counts.new + counts.handled})</option>
              {STATUS_OPTIONS.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label} ({counts[s.value] || 0})
                </option>
              ))}
            </select>
          </label>
        </div>
      </div>

      {loading && <StatusMessage as="p" variant="loading" className="contact-msg__state">Loading enquiries…</StatusMessage>}
      {error && (
        <StatusMessage as="p" variant="error" className="contact-msg__state contact-msg__state--error">
          {error} <button type="button" className="contact-msg__btn" onClick={fetchData}>Retry</button>
        </StatusMessage>
      )}

      {!loading && !error && items.length === 0 && (
        <StatusMessage as="p" variant="empty" className="contact-msg__state">
          {urlStatus
            ? 'No enquiries with that status.'
            : 'No enquiries yet. Anything sent through the contact form appears here as well as by email.'}
        </StatusMessage>
      )}

      {!loading && !error && items.length > 0 && (
        <div className="contact-msg__list">{items.map(card)}</div>
      )}
    </>
  );
}
